import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import AsyncSelect from 'react-select/async';

import ClickableIcon from '~components/ClickableIcon';
import { faRefresh } from '~components/icon';

import { InputProps } from '../props';

interface Option {
  value: string | number | undefined;
  label: string | undefined;
}

const SelectLazy: React.FC<InputProps> = ({
  id,
  value,
  disabled,
  placeholder,
  selectorOptions,
  selectorActions,
  selectorParams,
  onChange,
  clearable = true,
  manualReload = false,
  initialValue,
  initialOption,
}) => {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const fetchedSelectorOptions = useAppSelector(selectorOptions || (() => []));

  const options = !initialOption
    ? fetchedSelectorOptions.map(({ id, name }) => ({ value: id, label: name }) as Option)
    : [...fetchedSelectorOptions, initialOption].map(({ id, name }) => ({ value: id, label: name }) as Option);

  const emptyValue = { value: '', label: '-' };

  const [valueToDisplay, setValueToDisplay] = useState<Option | { label: string; value: string | null }>(emptyValue);

  const loadOptions = async (inputValue: string) => {
    const updatedOptions = options.filter((option) => option.label?.toLowerCase().includes(inputValue.toLowerCase()));
    return updatedOptions;
  };

  const refreshOptions = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    try {
      if (selectorActions) {
        await dispatch(selectorActions(selectorParams));
        setOptionsLoaded(true);
      }
    } catch (error: any) {
      console.error(`Error loading options for ${id}: ${error}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (initialOption === undefined && initialValue === undefined) {
      const selectedOption = options.find((option) => option.value === value);
      setValueToDisplay(selectedOption ?? emptyValue);
    } else if (optionsLoaded) {
      const selectedOption = options.find((option) => option.value === value);
      setValueToDisplay(selectedOption ?? emptyValue);
    } else if (initialValue) {
      setValueToDisplay(initialValue);
    } else if (value) {
      const selectedOption = options.find((option) => option.value === value);
      setValueToDisplay(selectedOption ?? emptyValue);
    } else {
      setValueToDisplay(emptyValue);
    }
  }, [fetchedSelectorOptions, optionsLoaded, value, initialValue, initialOption]);

  const handleFocus = () => {
    if (!optionsLoaded) {
      refreshOptions();
    }
  };

  const handleManualReload = () => {
    refreshOptions();
  };

  return (
    <div className="align-items-center row" data-cy={`${id}-selectlazy`}>
      <div className="col">
        <AsyncSelect
          id={id}
          name={id}
          classNamePrefix={'form-input-select'}
          isDisabled={disabled}
          placeholder={placeholder ?? '-'}
          isClearable={clearable}
          value={valueToDisplay}
          loadOptions={loadOptions}
          onFocus={handleFocus}
          onChange={(selected, event) => {
              onChange && onChange(event.name ?? '', selected ? selected?.value : null);
          }}
          defaultOptions={options}
          cacheOptions
        />
      </div>
      {manualReload && (
        <div className="col-sm-1 ms-2">
          <ClickableIcon
            id={id}
            icon={faRefresh}
            size="small"
            color="success"
            onClick={handleManualReload}
            disabled={isRefreshing}
            toolTip="Reload"
            dataCy={`${id}-reload`}
          />
        </div>
      )}
    </div>
  );
};

export default SelectLazy;
